import { useMutation, useQuery } from '@/hooks'
import { useQueryClient } from '@tanstack/react-query'
import {
  BILL_LOCATION_VALUES,
  type BillLocationValue,
} from '@/constants/bill-location'
import type {
  TempWorkerPaymentSlipAdvance,
  TempWorkerPaymentSlipSettlement,
} from '@/modules/temp-worker/services/tempWorker'

export type CompanyProfile = {
  legal_name: string
  address: string
  phone: string
  updated_at?: string
}

export type CompanyProfilesByLocation = Record<BillLocationValue, CompanyProfile>

export type CompanyProfilesResponse = {
  data: CompanyProfilesByLocation
}

export type UpdateCompanyProfilePayload = CompanyProfile & {
  location: BillLocationValue
}

export const useCompanyProfiles = () => {
  return useQuery<CompanyProfilesResponse>({
    queryKey: ['company_profile'],
    url: '/company-profile',
  })
}

export const useUpdateCompanyProfile = () => {
  const qc = useQueryClient()
  return useMutation<CompanyProfilesResponse, Error, UpdateCompanyProfilePayload>(
    ['company_profile_update'],
    { url: '/company-profile', method: 'PATCH' },
    {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['company_profile'] })
      },
    },
  )
}

/** Pick receipt header location for temp-worker payment slips. */
export function pickSlipHeaderLocation(
  slip: TempWorkerPaymentSlipSettlement | TempWorkerPaymentSlipAdvance,
): BillLocationValue {
  if (slip.kind === 'advance') return 'chanthaburi'

  const counts: Partial<Record<BillLocationValue, number>> = {}
  for (const row of slip.sessions) {
    const loc = row.location
    if (loc === 'chanthaburi' || loc === 'chumphon') {
      counts[loc] = (counts[loc] ?? 0) + 1
    }
  }

  let best: BillLocationValue = 'chanthaburi'
  let bestCount = 0
  for (const loc of BILL_LOCATION_VALUES) {
    const n = counts[loc] ?? 0
    if (n > bestCount) {
      bestCount = n
      best = loc
    }
  }
  return best
}

export function companyProfileForSlip(
  profiles: CompanyProfilesByLocation | undefined,
  slip: TempWorkerPaymentSlipSettlement | TempWorkerPaymentSlipAdvance,
): CompanyProfile | undefined {
  if (!profiles) return undefined
  return profiles[pickSlipHeaderLocation(slip)]
}
